import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import { Item, ItemWithMetaData } from "../api/Items";
import { CreateProfileRequest } from "../api/Profiles";
import {
  useCreateMultipleProfile,
  useRemoveMultipleProfile,
} from "../api/ProfilesApi";
import { ReportSummaryItemApi } from "../api/Reports";
import { useProfileManualRateDone } from "../api/WorkflowApi";
import ProfileAdd from "../profiles/ProfileAdd";
import Button from "../shared-components/Button";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { TrashOutlineIcon } from "../shared-components/icons";
import Divider from "../shared-components/LineDivider";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { AmendmentProfileRemoveRow } from "./AmendmentProfileRemoveRow";
import { useReportDetail } from "../reports/ReportDetailProvider";

type AmendmentEditProfilesProps = {
  report?: ReportSummaryItemApi;
  isReadOnly?: boolean;
};

interface UuidAndUrl {
  uuid: string;
  url: string;
}

function ProfileListFromSet({
  itemUUIDs,
  items,
}: {
  items: UuidAndUrl[];
  itemUUIDs: Set<string>;
}) {
  const list: UuidAndUrl[] = [];
  itemUUIDs.forEach((uuid) => {
    const item = items.find((item) => item.uuid === uuid);
    list.push({
      uuid: item?.url || "",
      url: item?.url || "",
    });
  });

  return (
    <ul className="my-3">
      {list.map((pivot) => (
        <li
          className="mb-2 p-3 rounded-lg bg-gray-100 last:mb-0"
          key={pivot.uuid}
        >
          <div className="flex gap-2 text-xs">
            <strong>ID:</strong>
            <span>{pivot.uuid}</span>
          </div>
          <div className="mt-1 font-semibold">{pivot.url ?? "N/A"}</div>
        </li>
      ))}
    </ul>
  );
}

function useAmendmentRemoveProfile() {
  const { hide, modal, show } = useConfirmationDialog();

  const {
    isLoading: loadingRemoveProfile,
    mutate: removeProfiles,
    error: errorRemoveProfile,
    isSuccess: successRemoveProfile,
  } = useRemoveMultipleProfile();

  useEffect(() => {
    if (errorRemoveProfile) show("Error Removing profile", "", hide);
  }, [errorRemoveProfile, show, hide]);

  function handleRemove(itemsToRemove: Set<string>, items: UuidAndUrl[]) {
    show(
      "Are you sure you want to delete the selected profiles",
      "",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          const itemUUIDs = Array.from(itemsToRemove.values());
          removeProfiles(itemUUIDs);
        }
        hide();
      },
      <ProfileListFromSet items={items} itemUUIDs={itemsToRemove} />
    );
  }

  return {
    loadingRemoveProfile,
    handleRemove,
    modal,
    errorRemoveProfile,
    successRemoveProfile,
  };
}

const AmendmentProfilesRemoveColumn = ({
  report,
}: {
  report: ReportSummaryItemApi;
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToRemove, setItemsToRemove] = useState<Set<string>>(new Set());
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const { loadingRemoveProfile, handleRemove, modal, successRemoveProfile } =
    useAmendmentRemoveProfile();
  const { isLoadingProfiles, errorLoadingProfiles, profileList, loadProfiles } =
    useReportDetail();

  useEffect(() => {
    const paramsPage = searchParams.get("page");
    if (!paramsPage) return;

    if (+paramsPage !== currentPage) {
      setCurrentPage(Number(searchParams.get("page")));
    }
  }, [currentPage, searchParams]);

  useEffect(() => {
    if (!loadingRemoveProfile && successRemoveProfile) {
      loadProfiles?.();
      setItemsToRemove(new Set());
    }
  }, [successRemoveProfile, loadingRemoveProfile, loadProfiles]);

  function updateRemoveCheckboxSelection(
    checked: boolean,
    item: ItemWithMetaData
  ) {
    if (checked) {
      itemsToRemove.add(item.item.external_id);
    } else {
      itemsToRemove.delete(item.item.external_id);
    }
    const uuidList = Array.from(itemsToRemove.values());

    setItemsToRemove(new Set(uuidList));
  }

  function onRemove() {
    if (profileList)
      handleRemove(
        itemsToRemove,
        profileList.map((item) => ({
          uuid: item.item.external_id,
          url: item.item.content.url,
        }))
      );
  }

  if (errorLoadingProfiles)
    return <ErrorMessageContainer message="Error getting the profiles" />;

  return (
    <div className="rounded-lg bg-gray-50 p-4">
      <div className="flex justify-between">
        <span className="text-xl font-bold">Remove Profile</span>
        {itemsToRemove.size > 0 && (
          <span className="pr-4" onClick={onRemove}>
            <TrashOutlineIcon />
          </span>
        )}
      </div>
      <div className="mt-4">
        {profileList?.map((item) => (
          <AmendmentProfileRemoveRow
            key={item.item.external_id}
            item={item}
            handleChange={(checked) =>
              updateRemoveCheckboxSelection(checked, item)
            }
          />
        ))}
      </div>
      {isLoadingProfiles && <LoadingSpinner className="relative my-4" />}
      {modal}
    </div>
  );
};

const AmendmentProfilesAddColumn = ({
  isReadOnly = false,
  report,
  handleProfiles,
}: {
  report: ReportSummaryItemApi;
  isReadOnly?: boolean;
  handleProfiles: (profiles: UuidAndUrl[]) => void;
}) => {
  const [profiles, setProfiles] = useState<ItemWithMetaData[]>([]);

  function updateProfiles(profiles: ItemWithMetaData[]) {
    setProfiles(profiles);
    handleProfiles(
      profiles.map((pivot) => ({
        url: pivot.item.content.url,
        uuid: pivot.item.external_id,
      }))
    );
  }

  const handleManualAdd = (inputUrl: string) => {
    if (!report?.report_uuid || !inputUrl) return;
    // built empty Item with metada for the sake of iteration
    const customItem: Item = {
      external_id: uuidv4(),
      in_report: true,
      manually_added: true,
      content: { url: inputUrl },
      is_reviewed: true,
    };
    updateProfiles([
      ...profiles,
      {
        entities: [],
        item: customItem,
        rating: [],
        score: [],
        type: { external_id: "", name: "" },
      },
    ]);
  };

  function handleRemove(item: ItemWithMetaData) {
    const list = profiles.filter(
      (profile) => profile.item.external_id !== item.item.external_id
    );
    updateProfiles(list);
  }

  return (
    <div className="rounded-lg bg-gray-50 p-4">
      <span className="text-xl font-bold">Add Profile</span>
      <div className="mt-4">
        {!isReadOnly && (
          <>
            <ProfileAdd
              handleAddProfile={(inputUrl) => handleManualAdd(inputUrl)}
            />
            <Divider />

            {profiles.map((item) => (
              <AmendmentProfileRemoveRow
                key={item.item.external_id}
                item={item}
                trashIconForRemove={true}
                handleChange={() => handleRemove(item)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export const AmendmentEditProfiles = ({
  isReadOnly = false,
  report,
}: AmendmentEditProfilesProps) => {
  const {
    mutate: createProfiles,
    isLoading: loadingCreateProfile,
    error: errorCreatingProfile,
    isSuccess: successCreateProfile,
  } = useCreateMultipleProfile();
  const [profilesToCreate, setProfilesToCreate] = useState<
    CreateProfileRequest[]
  >([]);
  const {
    isLoading: isLoadingProfileManualRateDone,
    error: errorProfileManualRate,
    mutate: profileManualRateDone,
    isSuccess: successProfileManualRateDone,
  } = useProfileManualRateDone();
  const navigate = useNavigate();
  useEffect(() => {
    if (report?.report_uuid && successCreateProfile) {
      profileManualRateDone({
        reportUUID: report.report_uuid,
        workflowRunUUID: report.workflow_run_uuid,
      });
    }
  }, [profileManualRateDone, report, successCreateProfile]);

  useEffect(() => {
    if (successProfileManualRateDone) {
      navigate("/reports/amendment", { state: { stepDone: true } });
    }
  }, [navigate, successProfileManualRateDone]);

  if (!report)
    return (
      <div data-testid="empty-reports">
        <LoadingSpinner className="relative mt-2" />
      </div>
    );

  function handleProfiles(list: UuidAndUrl[]) {
    if (!report) return;
    const profiles: CreateProfileRequest[] = list.map((pivot) => ({
      profile_url: pivot.url,
      report_uuid: report?.report_uuid,
      actor_uuid: HARDCODED_ACTOR_UUID,
    }));
    setProfilesToCreate(profiles);
  }

  function completeProfileReview() {
    createProfiles(profilesToCreate);
  }

  return (
    <div className="mt-4">
      <div className="grid grid-cols-2 gap-6">
        <AmendmentProfilesRemoveColumn report={report} />
        <AmendmentProfilesAddColumn
          report={report}
          handleProfiles={handleProfiles}
          isReadOnly={isReadOnly}
        />
      </div>
      <div className="flex justify-end pt-4">
        {!isReadOnly && (
          <Button
            text={"Complete Profile Review"}
            onClick={completeProfileReview}
          />
        )}
      </div>
      {(loadingCreateProfile || isLoadingProfileManualRateDone) && (
        <LoadingSpinner />
      )}
      {errorCreatingProfile && (
        <ErrorMessageContainer message="Error Creating Profiles" />
      )}
      {errorProfileManualRate && (
        <ErrorMessageContainer message="Error updating the workflow" />
      )}
    </div>
  );
};
